/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Yamato B2 Cloud API Integration (via Backend API)
 */
import axios from 'axios';
import type { YamatoInvoiceFormData } from '@/types/yamato';
import { getBaseUrl } from '@/libs/env';
import { getAccessToken } from '@/libs/firebaseAuth';

/**
 * Backend API Request Body
 */
interface YamatoInvoiceCreateRequest {
  serviceType: string;
  isCool?: string;
  shipmentDate: string; // YYYY/MM/DD format
  deliveryDate?: string; // YYYY/MM/DD format or '最短日'
  deliveryTimeZone?: string;
  amount?: string;
  taxAmount?: string;
  isPrintingLot?: string;
  packageQty?: string;
  invoiceCode: string;
  invoiceCodeExt?: string;
  invoiceFreightNo: string;
  shipmentNumber?: number;

  // Shipper info
  shipperCode?: string;
  shipperTelephone?: string;
  shipperTelephoneDisplay: string;
  shipperTelephoneExt?: string;
  shipperName: string;
  shipperNameKana?: string;
  shipperZipCode: string;
  shipperAddress?: string;
  shipperAddress1: string;
  shipperAddress2: string;
  shipperAddress3?: string;
  shipperAddress4?: string;

  // Consignee info
  consigneeCode?: string;
  consigneeTelephone?: string;
  consigneeTelephoneDisplay: string;
  consigneeTelephoneExt?: string;
  consigneeName: string;
  consigneeNameKana?: string;
  consigneeTitle?: string;
  consigneeZipCode: string;
  consigneeAddress?: string;
  consigneeAddress1: string;
  consigneeAddress2: string;
  consigneeAddress3?: string;
  consigneeAddress4?: string;
  consigneeDepartment1?: string;
  consigneeDepartment2?: string;

  // Center service
  isUsingCenterService?: string;
  consigneeCenterCode?: string;

  // Item info
  itemCode1?: string;
  itemName1: string;
  itemCode2?: string;
  itemName2?: string;
  handlingInformation1?: string;
  handlingInformation2?: string;
  note?: string;
}

/**
 * Backend API Success Response
 */
interface YamatoInvoiceCreateResponse {
  redirectUrl: string;
  message?: string;
}

/**
 * Backend API Error Response (Validation)
 */
interface YamatoValidationErrorResponse {
  error: 'Validation failed';
  message: Record<string, string>;
}

/**
 * Backend API Error Response (Yamato API Error)
 */
interface YamatoApiErrorResponse {
  error: 'Yamato API error';
  statusCode: number;
  errorCode?: string;
  errorDescription?: string;
  errors?: Array<{
    error_property_name: string;
    error_code: string;
    error_description: string;
  }>;
  message: string;
}

/**
 * Map YamatoInvoiceFormData to Backend API format
 * Note: Date format is kept as YYYY/MM/DD (not converted to YYYYMMDD)
 */
export function mapToBackendRequestFormat(
  data: YamatoInvoiceFormData
): YamatoInvoiceCreateRequest {
  // Combine address
  const shipperAddress = `${data.shipperAddress1 || ''}${data.shipperAddress2 || ''}${data.shipperAddress3 || ''}`;
  const consigneeAddress = `${data.consigneeAddress1 || ''}${data.consigneeAddress2 || ''}${data.consigneeAddress3 || ''}`;

  return {
    // Basic info
    shipmentNumber: data.shipmentNumber || undefined,
    serviceType: data.serviceType,
    isCool: data.isCool || '0',
    shipmentDate: data.shipDate, // Keep YYYY/MM/DD format
    deliveryDate: data.deliveryDate || '最短日',
    deliveryTimeZone: data.deliveryTimeZone || '',

    // Collection (COD)
    amount: data.amount,
    taxAmount: data.taxAmount,

    // Printing
    isPrintingLot: data.isPrintingLot,
    packageQty: data.packageQty,

    // Billing
    invoiceCode: data.invoiceCode || '',
    invoiceCodeExt: data.invoiceCodeExt,
    invoiceFreightNo: data.invoiceFreightNo || '',

    // Shipper (GMS)
    shipperCode: data.shipperCode,
    shipperTelephone: data.shipperTelephoneDisplay?.replace(/-/g, ''),
    shipperTelephoneDisplay: data.shipperTelephoneDisplay || '',
    shipperTelephoneExt: data.shipperTelephoneExt,
    shipperName: data.shipperName || '',
    shipperNameKana: data.shipperNameKana,
    shipperZipCode: data.shipperZipCode || '',
    shipperAddress: shipperAddress,
    shipperAddress1: data.shipperAddress1 || '',
    shipperAddress2: data.shipperAddress2 || '',
    shipperAddress3: data.shipperAddress3,
    shipperAddress4: data.shipperAddress4,

    // Consignee
    consigneeCode: data.consigneeCode,
    consigneeTelephone: data.consigneeTelephoneDisplay?.replace(/-/g, ''),
    consigneeTelephoneDisplay: data.consigneeTelephoneDisplay || '',
    consigneeTelephoneExt: data.consigneeTelephoneExt,
    consigneeName: data.consigneeName || '',
    consigneeNameKana: data.consigneeNameKana,
    consigneeTitle: data.consigneeTitle,
    consigneeZipCode: data.consigneeZipCode || '',
    consigneeAddress: consigneeAddress,
    consigneeAddress1: data.consigneeAddress1 || '',
    consigneeAddress2: data.consigneeAddress2 || '',
    consigneeAddress3: data.consigneeAddress3,
    consigneeAddress4: data.consigneeAddress4,
    consigneeDepartment1: data.consigneeDepartment1,
    consigneeDepartment2: data.consigneeDepartment2,

    // Center service
    isUsingCenterService: data.isUsingCenterService || '0',
    consigneeCenterCode: data.consigneeCenterCode,

    // Items
    itemCode1: data.itemCode1,
    itemName1: data.itemName1 || '',
    itemCode2: data.itemCode2,
    itemName2: data.itemName2,
    handlingInformation1: data.handlingInformation1,
    handlingInformation2: data.handlingInformation2,
    note: data.note
  };
}

/**
 * Submit Yamato invoice via Backend API
 */
export async function submitYamatoInvoice(
  data: YamatoInvoiceFormData
): Promise<YamatoInvoiceCreateResponse> {
  const requestData = mapToBackendRequestFormat(data);

  try {
    const response = await axios.post<YamatoInvoiceCreateResponse>(
      `${getBaseUrl()}/ship/yamato/create`,
      requestData,
      {
        headers: { auth: await getAccessToken() }
      }
    );

    const result = response.data;

    // Open Yamato portal page on success
    if (result.redirectUrl) {
      window.open(result.redirectUrl, '_blank');
    }

    return result;
  } catch (e: any) {
    // Handle validation errors (400)
    if (e.response?.status === 400) {
      const responseData = e.response.data;
      const validationError = responseData as YamatoValidationErrorResponse;
      if (validationError.error === 'Validation failed') {
        const errorMessages = Object.entries(validationError.message)
          .map(([field, message]) => `${field}: ${message}`)
          .join('\n');
        throw new Error(errorMessages);
      }
    }

    // Handle Yamato API errors (400 or 500)
    if (e.response?.status === 400 || e.response?.status === 500) {
      const responseData = e.response.data;
      const apiError = responseData as YamatoApiErrorResponse;
      if (apiError.error === 'Yamato API error') {
        // Keep structured error payload so UI can display `errors[]`, `errorCode`, ...
        throw apiError;
      }
    }

    // Re-throw other errors
    throw e;
  }
}

/**
 * Open Yamato B2 Cloud portal page
 * @param redirectUrl - Redirect URL from backend API response
 * @deprecated This function is no longer used as redirectUrl comes from backend
 */
export function openYamatoPortal(redirectUrl: string): void {
  window.open(redirectUrl, '_blank');
}
